/**
 * Foulmarkings wishlist — TASK-0012
 *
 * Handles:
 *   - Guest and logged-in storage via localStorage
 *   - Server sync via wishlist Lambda (endpoint from data attribute) when logged in
 *   - Merge-on-login, clear-on-logout
 *   - 200-item cap with user-visible message
 *   - 10/min rate limit per session
 *   - Deduplication by variant ID
 *   - Heart button state management (heart → checkmark on add)
 *   - Wishlist page rendering (product cards with image, title, price)
 */

(function () {
  'use strict';

  var _originalWishlistHeading = null;

  // ---------------------------------------------------------------------------
  // Constants
  // ---------------------------------------------------------------------------

  var WISHLIST_KEY = 'wishlist';
  var MAX_ITEMS = 200;
  var RATE_LIMIT = 10;
  var RATE_LIMIT_WINDOW_MS = 60 * 1000;

  // ---------------------------------------------------------------------------
  // Rate-limit state (in-memory, reset after 60s)
  // ---------------------------------------------------------------------------

  var _rateCount = 0;
  var _rateWindowStart = Date.now();

  function _checkRateLimit() {
    var now = Date.now();
    if (now - _rateWindowStart > RATE_LIMIT_WINDOW_MS) {
      _rateCount = 0;
      _rateWindowStart = now;
    }
    return _rateCount < RATE_LIMIT;
  }

  function _incrementRateCount() {
    _rateCount += 1;
  }

  // ---------------------------------------------------------------------------
  // localStorage helpers
  // ---------------------------------------------------------------------------

  function _readLocal() {
    try {
      var raw = localStorage.getItem(WISHLIST_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_e) {
      return [];
    }
  }

  function _writeLocal(items) {
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
    } catch (_e) {
      // Storage quota exceeded — silently skip
    }
  }

  function _dedup(items) {
    var seen = {};
    var result = [];
    for (var i = 0; i < items.length; i++) {
      var id = items[i].variantId;
      if (id && !seen[id]) {
        seen[id] = true;
        result.push(items[i]);
      }
    }
    return result;
  }

  // ---------------------------------------------------------------------------
  // Core API
  // ---------------------------------------------------------------------------

  /**
   * addToWishlist — stores a variant in localStorage with optional product data.
   * Returns 'added', 'duplicate', 'limit', or 'rate_limited'.
   *
   * productData: { title, url, image, price } — all optional, captured from data attributes
   */
  function addToWishlist(variantId, productData) {
    if (!_checkRateLimit()) {
      _showRateLimitMsg();
      return 'rate_limited';
    }

    var items = _readLocal();
    items = _dedup(items);

    // Check duplicate
    for (var i = 0; i < items.length; i++) {
      if (items[i].variantId === variantId) {
        return 'duplicate';
      }
    }

    // Check cap
    if (items.length >= MAX_ITEMS) {
      _showLimitMsg();
      return 'limit';
    }

    _incrementRateCount();

    var entry = { variantId: variantId, addedAt: Date.now() };
    if (productData) {
      if (productData.id) entry.id = productData.id;
      if (productData.title) entry.title = productData.title;
      if (productData.url) entry.url = productData.url;
      if (productData.image) entry.image = productData.image;
      if (productData.price) entry.price = productData.price;
      if (productData.creator) entry.creator = productData.creator;
    }
    items.push(entry);
    _writeLocal(items);
    _updateHeartStates();

    // If logged in, post to server
    var endpoint = _getLambdaEndpoint();
    if (endpoint && _isLoggedIn()) {
      _postToServer(endpoint, 'add', variantId, productData ? productData.title : '', productData ? productData.id : '');
    }

    return 'added';
  }

  /**
   * removeFromWishlist — removes a variant from localStorage (and server if logged in).
   */
  function removeFromWishlist(variantId) {
    var items = _readLocal();
    items = items.filter(function (item) {
      return item.variantId !== variantId;
    });
    _writeLocal(items);
    _updateHeartStates();
    _removeWishlistItemFromDom(variantId);

    var endpoint = _getLambdaEndpoint();
    if (endpoint && _isLoggedIn()) {
      _postToServer(endpoint, 'remove', variantId, '');
    }
  }

  /**
   * clearLocalWishlist — removes the localStorage key entirely.
   */
  function clearLocalWishlist() {
    try {
      localStorage.removeItem(WISHLIST_KEY);
    } catch (_e) {
      // ignore
    }
  }

  /**
   * mergeAndSync — called when a customer logs in.
   * Reads localStorage items, posts them to the server, then clears localStorage.
   */
  function mergeAndSync() {
    var localItems = _readLocal();
    if (!localItems.length) {
      clearLocalWishlist();
      return;
    }
    var endpoint = _getLambdaEndpoint();
    if (!endpoint) {
      clearLocalWishlist();
      return;
    }
    fetch(endpoint + '/merge', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: localItems }),
    })
      .then(function () {
        clearLocalWishlist();
      })
      .catch(function () {
        // Network error — clear anyway to avoid double-adding on retry
        clearLocalWishlist();
      });
  }

  // ---------------------------------------------------------------------------
  // Server communication
  // ---------------------------------------------------------------------------

  function _getLambdaEndpoint() {
    var el = document.querySelector('[data-wishlist-endpoint]');
    return el ? el.getAttribute('data-wishlist-endpoint') : null;
  }

  function _isLoggedIn() {
    return document.body.hasAttribute('data-customer-logged-in') ||
      document.querySelector('[data-customer-id]') !== null;
  }

  function _postToServer(endpoint, action, variantId, productTitle, productId) {
    // Append '/' so the API mapping resolves POST / correctly (bare /wishlist → 404)
    var payload = { action: action, variantId: variantId };
    if (productTitle) payload.productTitle = productTitle;
    if (productId) payload.productId = productId;
    fetch(endpoint + '/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).catch(function () {
      // Network failure — local state is still correct
    });
  }

  // ---------------------------------------------------------------------------
  // UI helpers
  // ---------------------------------------------------------------------------

  function _showLimitMsg() {
    var msgs = document.querySelectorAll('[data-wishlist-limit], [data-wishlist-error]');
    for (var i = 0; i < msgs.length; i++) {
      msgs[i].removeAttribute('hidden');
    }
  }

  function _showRateLimitMsg() {
    var msgs = document.querySelectorAll('[data-wishlist-rate-limit-msg]');
    for (var i = 0; i < msgs.length; i++) {
      msgs[i].removeAttribute('hidden');
      var msg = msgs[i];
      setTimeout(function () {
        msg.setAttribute('hidden', '');
      }, 5000);
    }
  }

  function _updateHeartStates() {
    var items = _readLocal();
    var wishlisted = {};
    for (var i = 0; i < items.length; i++) {
      wishlisted[items[i].variantId] = true;
    }

    var hearts = document.querySelectorAll('[data-wishlist-heart]');
    for (var j = 0; j < hearts.length; j++) {
      var heart = hearts[j];
      var vid = heart.getAttribute('data-variant-id');
      if (wishlisted[vid]) {
        heart.classList.add('is-wishlisted');
        heart.setAttribute('aria-pressed', 'true');
        heart.setAttribute('aria-label', 'Remove from wishlist');
      } else {
        heart.classList.remove('is-wishlisted');
        heart.setAttribute('aria-pressed', 'false');
        heart.setAttribute('aria-label', heart.getAttribute('data-add-label') || 'Add to wishlist');
      }
    }

    // Keep share row and guest banner in sync on the wishlist page
    if (document.querySelector('[data-section="wishlist"]')) {
      _maybeShowShareRow();
      _maybeShowGuestBanner();
    }

    _updateWishlistBadge();
  }

  // Mirrors the cart icon's count bubble (#cart-icon-bubble .cart-count-bubble):
  // same markup shape and CSS class, populated from the deduped localStorage
  // wishlist instead of cart.item_count since there's no server-rendered count.
  function _updateWishlistBadge() {
    var bubble = document.getElementById('wishlist-count-bubble');
    if (!bubble) return;

    var count = _dedup(_readLocal()).length;
    var countEl = bubble.querySelector('[data-wishlist-count]');
    var labelEl = bubble.querySelector('[data-wishlist-count-label]');

    if (count > 0) {
      bubble.style.display = '';
      if (countEl) countEl.textContent = count < 100 ? String(count) : '';
      if (labelEl) labelEl.textContent = count + (count === 1 ? ' item in wishlist' : ' items in wishlist');
    } else {
      bubble.style.display = 'none';
      if (countEl) countEl.textContent = '';
      if (labelEl) labelEl.textContent = '';
    }
  }

  function _removeWishlistItemFromDom(variantId) {
    var items = document.querySelectorAll('[data-wishlist-item][data-variant-id]');
    for (var i = 0; i < items.length; i++) {
      if (items[i].getAttribute('data-variant-id') === variantId) {
        items[i].remove();
      }
    }
    _maybeShowEmptyState();
  }

  function _maybeShowEmptyState() {
    var list = document.getElementById('wishlist-items-list');
    if (!list) return;
    var remaining = list.querySelectorAll('[data-wishlist-item]');
    var empty = document.querySelector('[data-wishlist-empty]');
    if (remaining.length === 0 && empty) {
      empty.removeAttribute('hidden');
    }
    _maybeShowShareRow();
    _maybeShowGuestBanner();
  }

  // ---------------------------------------------------------------------------
  // Wishlist page rendering
  // ---------------------------------------------------------------------------

  function _renderWishlistPage() {
    var container = document.querySelector('[data-wishlist-items-container]');
    if (!container) return;

    var loadingEl = container.querySelector('[data-wishlist-guest-loading]');
    var list = container.querySelector('#wishlist-items-list');
    var emptyEl = container.querySelector('[data-wishlist-empty]');

    var items = _dedup(_readLocal());

    var headingEl = document.querySelector('[data-wishlist-heading]');
    if (headingEl && !_originalWishlistHeading) {
      _originalWishlistHeading = headingEl.textContent.trim();
    }

    if (loadingEl) loadingEl.setAttribute('hidden', '');

    if (!items.length) {
      if (list) list.setAttribute('hidden', '');
      if (emptyEl) emptyEl.removeAttribute('hidden');
      if (headingEl) headingEl.textContent = 'Your Wishlist is Empty';
      return;
    }

    if (emptyEl) emptyEl.setAttribute('hidden', '');
    if (headingEl && _originalWishlistHeading) headingEl.textContent = _originalWishlistHeading;

    if (!list) return;
    list.removeAttribute('hidden');
    list.innerHTML = '';

    items.forEach(function (item) {
      var li = _buildCard(item);
      list.appendChild(li);
      _enrichCardFromApi(li, item);
    });
  }

  function _buildCard(item) {
    var variantId = item.variantId || '';
    var title = item.title || '';
    var url = item.url || '';
    var image = item.image || '';
    var price = item.price || '';
    var creator = item.creator || '';

    var li = document.createElement('li');
    li.className = 'wishlist__item';
    li.setAttribute('data-wishlist-item', '');
    li.setAttribute('data-variant-id', variantId);

    var removeBtn =
      '<button class="wishlist__remove-btn"' +
      ' data-wishlist-remove="' + _escapeHtml(variantId) + '"' +
      ' type="button"' +
      ' aria-label="Remove from wishlist">' +
      '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">' +
      '<path d="M1 1l10 10M11 1L1 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
      '</svg>' +
      '</button>';

    var mediaHtml = image
      ? '<div class="wishlist__item-media">' +
        (url ? '<a href="' + _escapeHtml(url) + '" tabindex="-1" aria-hidden="true">' : '') +
        '<img class="wishlist__item-image" src="' + _escapeHtml(image) + '" alt="" loading="lazy">' +
        (url ? '</a>' : '') +
        removeBtn +
        '</div>'
      : '<div class="wishlist__item-media wishlist__item-media--no-image">' + removeBtn + '</div>';

    var titleHtml = title
      ? (url
        ? '<a class="wishlist__item-title" href="' + _escapeHtml(url) + '">' + _escapeHtml(title) + '</a>'
        : '<span class="wishlist__item-title">' + _escapeHtml(title) + '</span>')
      : '<span class="wishlist__item-title">Saved item</span>';

    var priceHtml = price
      ? '<p class="wishlist__item-price">' + _escapeHtml(price) + '</p>'
      : '';

    var creatorHtml = creator
      ? '<p class="wishlist__item-creator">' + _escapeHtml(creator) + '</p>'
      : '';

    li.innerHTML =
      mediaHtml +
      '<div class="wishlist__item-body">' +
      titleHtml +
      creatorHtml +
      priceHtml +
      '<div class="wishlist__item-options" data-wishlist-options hidden></div>' +
      '<button class="btn btn--primary btn--full-width wishlist__atc-btn" data-wishlist-atc data-variant-id="' + _escapeHtml(variantId) + '" type="button">Add to cart</button>' +
      '</div>';

    return li;
  }

  function _enrichCardFromApi(li, item) {
    if (!item.url) return;

    var apiUrl = item.url.split('?')[0] + '.js';
    fetch(apiUrl)
      .then(function (res) { return res.ok ? res.json() : Promise.reject(); })
      .then(function (product) {
        _populateOptions(li, product, item.variantId);
        _updateCardPrice(li, product, item.variantId);
        _updateCardImage(li, product, item.variantId);
      })
      .catch(function () { /* silently skip if product unavailable */ });
  }

  function _updateCardImage(li, product, savedVariantId) {
    var imgEl = li.querySelector('.wishlist__item-image');
    if (!imgEl) return;

    // Prefer the variant's image if available, else the product's featured image
    var variant = product.variants && product.variants.find(function (v) {
      return String(v.id) === String(savedVariantId);
    });
    var src = (variant && variant.featured_image && variant.featured_image.src)
      || product.featured_image;
    if (!src) return;

    // Ensure protocol-relative URLs get https
    if (src.indexOf('//') === 0) src = 'https:' + src;

    imgEl.src = src;
    // Show the media wrapper in case it was hidden due to missing stored URL
    var mediaEl = li.querySelector('.wishlist__item-media--no-image');
    if (mediaEl) {
      mediaEl.classList.remove('wishlist__item-media--no-image');
      mediaEl.innerHTML = imgEl.outerHTML + mediaEl.innerHTML;
    }
  }

  function _populateOptions(li, product, savedVariantId) {
    if (!product.variants || product.variants.length <= 1) return;
    // Skip if every option has only one value (default variant with no real choices)
    var hasRealOptions = product.options && product.options.some(function (_, idx) {
      var vals = [];
      product.variants.forEach(function (v) {
        if (vals.indexOf(v.options[idx]) === -1) vals.push(v.options[idx]);
      });
      return vals.length > 1;
    });
    if (!hasRealOptions) return;

    var optionsEl = li.querySelector('[data-wishlist-options]');
    if (!optionsEl) return;

    var atcBtn = li.querySelector('[data-wishlist-atc]');
    optionsEl.innerHTML = '';

    // Build a map of variant id → variant
    var variantMap = {};
    product.variants.forEach(function (v) { variantMap[String(v.id)] = v; });

    // One <select> per option position — skip options with only one value
    // product.options may be strings or {name, values} objects depending on endpoint
    product.options.forEach(function (optionRaw, idx) {
      var optionName = typeof optionRaw === 'string' ? optionRaw : (optionRaw.name || ('Option ' + (idx + 1)));

      var values = [];
      product.variants.forEach(function (v) {
        var val = v.options[idx];
        if (values.indexOf(val) === -1) values.push(val);
      });

      if (values.length <= 1) return;

      var sel = document.createElement('select');
      sel.className = 'wishlist__item-option-select';
      sel.setAttribute('aria-label', optionName);
      sel.setAttribute('data-option-index', idx);

      // Placeholder — shows "Select <Option>" as default, disabled so it can't be re-chosen
      var placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = 'Select ' + optionName;
      placeholder.disabled = true;
      sel.appendChild(placeholder);

      values.forEach(function (val) {
        var opt = document.createElement('option');
        opt.value = val;
        opt.textContent = val;
        sel.appendChild(opt);
      });

      optionsEl.appendChild(sel);
    });

    // Pre-select the saved variant's options; if no match, leave placeholder selected
    var savedVariant = variantMap[String(savedVariantId)];
    var selects = optionsEl.querySelectorAll('select');
    if (savedVariant) {
      var selectIdx = 0;
      product.options.forEach(function (_, idx) {
        var values = [];
        product.variants.forEach(function (v) {
          if (values.indexOf(v.options[idx]) === -1) values.push(v.options[idx]);
        });
        if (values.length <= 1) return;
        if (selects[selectIdx]) selects[selectIdx].value = savedVariant.options[idx];
        selectIdx++;
      });
    } else {
      // No saved variant — show placeholder as selected on all selects
      Array.prototype.forEach.call(selects, function (s) { s.value = ''; });
    }

    optionsEl.removeAttribute('hidden');

    // When any select changes, resolve the matching variant and update ATC
    function _resolveVariant() {
      var selects = optionsEl.querySelectorAll('select');
      // Map back to full option array including single-value options
      var chosen = [];
      var selectIdx = 0;
      product.options.forEach(function (_, idx) {
        var values = [];
        product.variants.forEach(function (v) {
          if (values.indexOf(v.options[idx]) === -1) values.push(v.options[idx]);
        });
        if (values.length <= 1) {
          chosen.push(values[0]);
        } else {
          chosen.push(selects[selectIdx] ? selects[selectIdx].value : '');
          selectIdx++;
        }
      });

      // Don't resolve if any select still shows placeholder
      if (chosen.indexOf('') !== -1) return;

      var match = product.variants.find(function (v) {
        return v.options.every(function (o, i) { return o === chosen[i]; });
      });
      if (match && atcBtn) {
        atcBtn.setAttribute('data-variant-id', match.id);
        atcBtn.disabled = !match.available;
        atcBtn.textContent = match.available ? 'Add to cart' : 'Sold out';
      }
    }

    optionsEl.addEventListener('change', _resolveVariant);
    _resolveVariant();
  }

  function _updateCardPrice(li, product, savedVariantId) {
    var priceEl = li.querySelector('.wishlist__item-price');
    if (!priceEl) return;

    var variant = product.variants && product.variants.find(function (v) {
      return String(v.id) === String(savedVariantId);
    });
    var cents = variant ? variant.price : product.price_min;
    if (cents == null) return;

    var formatted = (cents / 100).toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });
    priceEl.textContent = formatted;
  }

  function _escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // ---------------------------------------------------------------------------
  // Event wiring
  // ---------------------------------------------------------------------------

  function _bindHeartButtons() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-wishlist-heart]');
      if (!btn) return;

      var variantId = btn.getAttribute('data-variant-id');
      if (!variantId) return;

      var isWishlisted = btn.classList.contains('is-wishlisted');
      if (isWishlisted) {
        removeFromWishlist(variantId);
      } else {
        var productData = {
          id: btn.getAttribute('data-product-id') || '',
          title: btn.getAttribute('data-product-title') || '',
          url: btn.getAttribute('data-product-url') || '',
          image: btn.getAttribute('data-product-image') || '',
          price: btn.getAttribute('data-product-price') || '',
          creator: btn.getAttribute('data-product-creator') || '',
        };
        addToWishlist(variantId, productData);
      }
    });
  }

  function _bindRemoveButtons() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-wishlist-remove]');
      if (!btn) return;

      var variantId = btn.getAttribute('data-wishlist-remove');
      if (!variantId) return;

      removeFromWishlist(variantId);
    });
  }

  function _bindAtcButtons() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-wishlist-atc]');
      if (!btn || btn.disabled) return;

      var variantId = btn.getAttribute('data-variant-id');
      if (!variantId) return;

      btn.disabled = true;
      btn.textContent = 'Adding...';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      })
        .then(function (res) { return res.ok ? res.json() : Promise.reject(); })
        .then(function () {
          btn.textContent = 'Added';
          // Refresh cart count if the theme exposes a cart bubble
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          setTimeout(function () {
            btn.disabled = false;
            btn.textContent = 'Add to cart';
          }, 2000);
        })
        .catch(function () {
          btn.disabled = false;
          btn.textContent = 'Add to cart';
        });
    });
  }

  function _bindLogout() {
    // Listen for logout link clicks to clear localStorage preemptively
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[href*="/account/logout"]');
      if (link) {
        clearLocalWishlist();
      }
    });

    // Also listen for a custom event dispatched by themes or apps
    document.addEventListener('customer:logout', function () {
      clearLocalWishlist();
    });
  }

  // ---------------------------------------------------------------------------
  // Share wishlist — copies the current page URL to clipboard
  // ---------------------------------------------------------------------------

  function _bindShareButton() {
    var btn = document.querySelector('[data-wishlist-copy-link]');
    var feedback = document.querySelector('[data-wishlist-copy-feedback]');
    if (!btn || btn.getAttribute('data-share-bound')) return;
    btn.setAttribute('data-share-bound', '1');

    btn.addEventListener('click', function () {
      var items = _dedup(_readLocal());
      if (!items.length) return;

      var endpoint = _getLambdaEndpoint();
      if (endpoint) {
        // Server path: POST /share → get a short UUID
        btn.disabled = true;
        fetch(endpoint + '/share', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ variantIds: items.map(function (i) { return i.variantId; }) }),
        })
          .then(function (res) { return res.ok ? res.json() : Promise.reject(); })
          .then(function (data) {
            var shareUrl = window.location.origin + window.location.pathname + '?w=' + encodeURIComponent(data.shareId);
            _copyToClipboard(shareUrl, feedback);
          })
          .catch(function () {
            // Fall back to variant IDs in URL
            _copyShareUrlWithIds(items, feedback);
          })
          .finally(function () {
            btn.disabled = false;
          });
      } else {
        _copyShareUrlWithIds(items, feedback);
      }
    });
  }

  function _copyShareUrlWithIds(items, feedback) {
    var ids = items.map(function (i) { return i.variantId; }).join(',');
    var shareUrl = window.location.origin + window.location.pathname + '?w=' + encodeURIComponent(ids);
    _copyToClipboard(shareUrl, feedback);
  }

  function _copyToClipboard(text, feedback) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(function () {
        _showCopyFeedback(feedback);
      }).catch(function () {
        _fallbackCopy(text, feedback);
      });
    } else {
      _fallbackCopy(text, feedback);
    }
  }

  function _fallbackCopy(text, feedback) {
    var ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    try { document.execCommand('copy'); } catch (_e) { /* silent */ }
    document.body.removeChild(ta);
    _showCopyFeedback(feedback);
  }

  function _showCopyFeedback(el) {
    if (!el) return;
    el.classList.add('is-visible');
    setTimeout(function () { el.classList.remove('is-visible'); }, 2200);
  }

  function _maybeShowShareRow() {
    var row = document.querySelector('[data-wishlist-share]');
    if (!row) return;
    var items = _dedup(_readLocal());
    if (items.length > 0) {
      row.removeAttribute('hidden');
    } else {
      row.setAttribute('hidden', '');
    }
  }

  // ---------------------------------------------------------------------------
  // Shared wishlist (?w= UUID param) — still supported for existing share links
  // ---------------------------------------------------------------------------

  function _handleSharedWishlist() {
    var params = new URLSearchParams(window.location.search);
    var shareId = params.get('w');
    if (!shareId) return;

    // Fallback format: comma-separated variant IDs (no Lambda needed)
    if (/^[\d,]+$/.test(shareId)) {
      var ids = shareId.split(',').filter(Boolean);
      if (ids.length) _showSharedBanner(ids);
      return;
    }

    // UUID format: resolve via Lambda
    if (/^[0-9a-f-]{36}$/.test(shareId)) {
      var endpoint = _getLambdaEndpoint();
      if (!endpoint) return;

      fetch(endpoint + '/share/' + encodeURIComponent(shareId))
        .then(function (res) { return res.ok ? res.json() : Promise.reject(res.status); })
        .then(function (data) {
          var ids = Array.isArray(data.variantIds) ? data.variantIds : [];
          if (!ids.length) return;
          _showSharedBanner(ids);
        })
        .catch(function () { /* expired or not found — silently skip */ });
    }
  }

  function _showSharedBanner(ids) {
    var banner = document.querySelector('[data-wishlist-shared-banner]');
    var countEl = document.querySelector('[data-wishlist-shared-count]');
    var addAllBtn = document.querySelector('[data-wishlist-add-all]');

    if (banner) {
      if (countEl) {
        var noun = ids.length === 1 ? 'item' : 'items';
        countEl.textContent = 'Someone shared a wishlist with you — ' + ids.length + ' ' + noun + '.';
      }
      banner.removeAttribute('hidden');
    }

    if (addAllBtn) {
      addAllBtn.addEventListener('click', function () {
        addAllBtn.disabled = true;
        addAllBtn.textContent = 'Adding...';

        // Fetch product data for each variant so cards render with real title/image/price
        var fetches = ids.map(function (variantId) {
          return fetch('/variants/' + encodeURIComponent(variantId) + '.js')
            .then(function (res) { return res.ok ? res.json() : Promise.reject(); })
            .then(function (variant) {
              var productData = {
                title: variant.title && variant.title !== 'Default Title' ? variant.title : (variant.product_title || ''),
                url: variant.url || '',
                image: (variant.featured_image && variant.featured_image.src) ? variant.featured_image.src : '',
                price: variant.price ? (variant.price / 100).toLocaleString('en-AU', { style: 'currency', currency: 'AUD' }) : '',
              };
              // Prefer product title over variant title
              if (variant.product_title) productData.title = variant.product_title;
              addToWishlist(variantId, productData);
            })
            .catch(function () {
              addToWishlist(variantId, null);
            });
        });

        Promise.all(fetches).then(function () {
          addAllBtn.textContent = 'Added';
          _dismissSharedBanner(banner);
          _renderWishlistPage();
          _maybeShowShareRow();
          _maybeShowGuestBanner();
        });
      });
    }
  }

  function _dismissSharedBanner(banner) {
    if (!banner) return;
    banner.style.overflow = 'hidden';
    banner.style.transition = 'max-height 0.35s ease, opacity 0.3s ease, margin-bottom 0.35s ease';
    banner.style.maxHeight = banner.scrollHeight + 'px';
    banner.style.opacity = '1';
    // Trigger reflow then animate to zero
    banner.offsetHeight; // eslint-disable-line no-unused-expressions
    banner.style.maxHeight = '0';
    banner.style.opacity = '0';
    banner.style.marginBottom = '0';
    setTimeout(function () { banner.setAttribute('hidden', ''); }, 380);
  }

  // ---------------------------------------------------------------------------
  // Guest save banner
  // ---------------------------------------------------------------------------

  function _maybeShowGuestBanner() {
    var banner = document.querySelector('[data-wishlist-guest-banner]');
    if (!banner) return;
    var items = _dedup(_readLocal());
    if (items.length > 0 && !_isLoggedIn()) {
      banner.removeAttribute('hidden');
    } else {
      banner.setAttribute('hidden', '');
    }
  }

  // ---------------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------------

  function _init() {
    // Deduplicate whatever is in localStorage on load
    var items = _readLocal();
    var deduped = _dedup(items);
    if (deduped.length !== items.length) {
      _writeLocal(deduped);
    }

    // Update heart icon states on all product cards visible on the page
    _updateHeartStates();

    // Render wishlist page if we are on /pages/wishlist
    var wishlistContainer = document.querySelector('[data-wishlist-items-container]');
    if (wishlistContainer) {
      _renderWishlistPage();
    }

    // Wishlist page extras (share, guest banner, shared ?w= param)
    var isWishlistPage = !!document.querySelector('[data-section="wishlist"]');
    if (isWishlistPage) {
      _handleSharedWishlist();
      _maybeShowShareRow();
      _maybeShowGuestBanner();
      _bindShareButton();
    }

    // Bind interaction handlers
    _bindHeartButtons();
    _bindRemoveButtons();
    _bindAtcButtons();
    _bindLogout();

    // If logged in and on a page load that might follow a login, merge localStorage
    var isLoginReturn = window.location.pathname.indexOf('/account') === 0 &&
      window.location.pathname.indexOf('/account/login') === -1;
    if (_isLoggedIn() && isLoginReturn && _readLocal().length > 0) {
      mergeAndSync();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', _init);
  } else {
    _init();
  }

  // Expose to global scope for other scripts that need to call mergeAndSync
  window.FMWishlist = {
    add: addToWishlist,
    remove: removeFromWishlist,
    clear: clearLocalWishlist,
    merge: mergeAndSync,
  };
})();
